import { capitalize } from 'lodash';
import { getTextThemeProvider } from '../../../venn-ui-kit/src/themes/src/getTextThemeProvider';
import { type DetailedProxyMetadata, type FrequencyEnum, type ProxyTypeEnum, ProxyTypeEnumValues } from 'venn-api';
import {
  type ProxyTypeDetails,
  type FirstLayerProxyTypeValue,
  type SecondLayerProxyTypeValue,
  createFirstLayerProxyTypeValue,
  getExtrapolateFromValue,
} from '../types';
import Dates from '../dates';
import { getFrequencyType, MOMENT_DURATIONS } from '../frequency';
import { assertExhaustive } from '../type';

export const getFormattedProxyType = (proxyType: ProxyTypeEnum, extrapolate: boolean = false): string => {
  let formattedProxyType: string;
  switch (proxyType) {
    case 'DESMOOTH_INTERPOLATE':
      formattedProxyType = 'Desmooth with Interpolation';
      break;
    default:
      formattedProxyType = capitalize(proxyType);
  }
  if (extrapolate) {
    formattedProxyType += ' + Extrapolate';
  }
  return formattedProxyType;
};

export const PROXY_TYPE_GROUPS: ProxyTypeDetails[][] = [
  [
    {
      value: createFirstLayerProxyTypeValue('BACKFILL', false),
      label: 'Backfill',
      description: 'Extend past history',
    },
    {
      value: createFirstLayerProxyTypeValue('BACKFILL', true),
      label: 'Backfill + Extrapolate',
      description: 'Extend past history and extend available history forward',
    },
  ],

  [
    {
      value: createFirstLayerProxyTypeValue('SUBSTITUTE', false),
      label: 'Substitute',
      description: 'Replace with a different investment',
    },
  ],

  [
    {
      value: createFirstLayerProxyTypeValue('DESMOOTH', false),
      label: 'Desmooth',
      description: 'Reduce private asset performance lag',
    },
    {
      value: createFirstLayerProxyTypeValue('DESMOOTH_INTERPOLATE', false),
      label: 'Desmooth with Interpolation',
      description: 'Reduce private asset performance lag and increase return frequency to daily',
    },
    {
      value: createFirstLayerProxyTypeValue('DESMOOTH', true),
      label: 'Desmooth + Extrapolate',
      description: 'Reduce private asset performance lag and extend available history forward',
    },
    {
      value: createFirstLayerProxyTypeValue('DESMOOTH_INTERPOLATE', true),
      label: 'Desmooth with Interpolation + Extrapolate',
      description:
        'Reduce private asset performance lag, increase return frequency to daily and extend available history forward',
    },
  ],

  [
    {
      value: createFirstLayerProxyTypeValue('INTERPOLATE', false),
      label: 'Interpolate',
      description: 'Increase return frequency to daily',
    },
    {
      value: createFirstLayerProxyTypeValue('INTERPOLATE', true),
      label: 'Interpolate + Extrapolate',
      description: 'Increase return frequency to daily and extend available history forward',
    },
  ],

  [
    {
      value: createFirstLayerProxyTypeValue('EXTRAPOLATE', false),
      label: 'Extrapolate',
      description: 'Extend performance forward',
    },
  ],
];

const proxyTypeValueKey = (value: FirstLayerProxyTypeValue | SecondLayerProxyTypeValue): string =>
  `${value.proxyType}-${getExtrapolateFromValue(value)}`;

const PROXY_TYPE_DETAILS_MAP = new Map<string, ProxyTypeDetails>(
  PROXY_TYPE_GROUPS.flat().map((detail) => [proxyTypeValueKey(detail.value), detail]),
);

export const getProxyTypeLabel = (value: FirstLayerProxyTypeValue | SecondLayerProxyTypeValue): string => {
  const detail = PROXY_TYPE_DETAILS_MAP.get(proxyTypeValueKey(value));
  return detail?.label ?? getFormattedProxyType(value.proxyType);
};

export type LayeredProxyTypeValidation = {
  hasOnlyErrors: boolean;
  hasSomeErrors: boolean;
  hasAnyValid: boolean;
  disabledMessage: string | undefined;
  investmentInfo: readonly {
    disabledMessage: string | undefined;
    isValid: boolean;
    investmentId: string;
  }[];
};

export const validateLayeredProxyTypeForInvestments = ({
  investments,
  proxy,
}: {
  investments: { frequency: FrequencyEnum; investmentSource: string; id: string }[];
  proxy: { proxyType: ProxyTypeEnum; extrapolate: boolean; proxyLayer: 'first' | 'second' | undefined };
}): LayeredProxyTypeValidation => {
  if (investments.length === 0) {
    return {
      hasOnlyErrors: false,
      hasSomeErrors: false,
      hasAnyValid: true,
      disabledMessage: undefined,
      investmentInfo: [],
    };
  }
  const validationResults = investments.map((investment) => ({
    ...validateLayeredProxyType({ investment, proxy }),
    investment,
  }));
  const errorValidationResults = validationResults.filter((vr) => !vr.isValid);
  return {
    hasAnyValid: errorValidationResults.length < validationResults.length,
    hasSomeErrors: errorValidationResults.length > 0,
    hasOnlyErrors: validationResults.length === errorValidationResults.length,
    disabledMessage: errorValidationResults[0]?.message,
    investmentInfo: validationResults.map((vr) => ({
      investmentId: vr.investment.id,
      disabledMessage: vr.message,
      isValid: vr.isValid,
    })),
  };
};

interface ValidateProxyTypeForInvestmentProps {
  investment: { frequency: FrequencyEnum; investmentSource: string };
  proxy: { proxyType: ProxyTypeEnum; extrapolate: boolean; proxyLayer: 'first' | 'second' | undefined };
}

export const validateLayeredProxyType = ({
  investment: { frequency, investmentSource },
  proxy: { proxyType, extrapolate, proxyLayer },
}: ValidateProxyTypeForInvestmentProps): { isValid: boolean; message?: string } => {
  if (proxyType === 'BACKFILL' && extrapolate && frequency === 'QUARTERLY') {
    return { isValid: false, message: 'Not supported for quarterly investments' };
  }

  if (proxyType === 'SUBSTITUTE' && investmentSource === 'VENN') {
    return { isValid: false, message: `Unavailable for ${getTextThemeProvider().brandProvidedLabel} investments` };
  }

  if (['DESMOOTH', 'DESMOOTH_INTERPOLATE'].includes(proxyType) && frequency === 'DAILY') {
    return { isValid: false, message: 'Unavailable for daily investments' };
  }

  if (proxyType === 'DESMOOTH' && extrapolate && frequency === 'QUARTERLY') {
    return { isValid: false, message: 'Requires interpolation for quarterly investments' };
  }

  if (proxyType === 'INTERPOLATE' && frequency === 'DAILY') {
    return { isValid: false, message: 'Unavailable for daily investments' };
  }

  if (proxyType === 'EXTRAPOLATE' && frequency === 'QUARTERLY') {
    return { isValid: false, message: 'Requires interpolation for quarterly investments' };
  }

  if (proxyLayer === 'second' && frequency === 'DAILY') {
    return { isValid: false, message: 'Unavailable for daily investments' };
  }

  return { isValid: true };
};

export const getProxyTypeDescription = (proxyType: ProxyTypeEnum): string => {
  switch (proxyType) {
    case 'BACKFILL':
      return 'Extend past history';
    case 'INTERPOLATE':
      return 'Increase return frequency to daily';
    case 'SUBSTITUTE':
      return 'Replace with different investment';
    case 'DESMOOTH':
      return 'Reduce private asset performance lag';
    case 'DESMOOTH_INTERPOLATE':
      return 'Reduce private asset performance lag and increase return frequency to daily';
    case 'EXTRAPOLATE':
      return 'Extend available history forward';
    default:
      throw assertExhaustive(proxyType, 'Unexpected proxy type');
  }
};

export const getProxyTypeNoun = (proxyType: ProxyTypeEnum): string => {
  switch (proxyType) {
    case 'BACKFILL':
      return 'Backfill';
    case 'INTERPOLATE':
      return 'Interpolate';
    case 'SUBSTITUTE':
      return 'Substitute';
    case 'DESMOOTH':
      return 'Desmooth';
    case 'DESMOOTH_INTERPOLATE':
      return 'Desmooth with Interpolation';
    case 'EXTRAPOLATE':
      return 'Extrapolate';
    default:
      throw assertExhaustive(proxyType, 'Unexpected proxy type');
  }
};

export const getProxyTypeNounsWithExtrapolate = (proxyType: ProxyTypeEnum, withExtrapolate?: boolean): string[] => {
  switch (proxyType) {
    case 'BACKFILL':
      return withExtrapolate ? ['Backfill', 'Backfill + Extrapolate'] : ['Backfill'];

    case 'INTERPOLATE':
      return ['Interpolate', 'Interpolate + Extrapolate'];

    case 'SUBSTITUTE':
      return ['Substitute'];

    case 'DESMOOTH':
      return withExtrapolate ? ['Desmooth', 'Desmooth + Extrapolate'] : ['Desmooth'];

    case 'DESMOOTH_INTERPOLATE':
      return ['Desmooth with Interpolation', 'Desmooth with Interpolation + Extrapolate'];

    case 'EXTRAPOLATE':
      return ['Extrapolate'];

    default:
      throw assertExhaustive(proxyType, 'Unexpected proxy type');
  }
};

export interface ProxyStartDateParams {
  proxyType: ProxyTypeEnum;
  proxiedStartDate: number;
  unproxiedEndDate: number;
  proxiedFrequency: FrequencyEnum;
}
export const getProxyStartDate = (proxy: ProxyStartDateParams): number => {
  switch (proxy.proxyType) {
    case 'BACKFILL':
    case 'INTERPOLATE':
    case 'SUBSTITUTE':
    case 'DESMOOTH':
    case 'DESMOOTH_INTERPOLATE':
      return proxy.proxiedStartDate;
    case 'EXTRAPOLATE':
      return (
        Dates.parse(proxy.unproxiedEndDate)
          // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
          .add(1, MOMENT_DURATIONS[getFrequencyType(proxy.proxiedFrequency)])
          .valueOf()
      );
    default:
      throw assertExhaustive(proxy.proxyType, 'Unexpected proxy type');
  }
};

export interface ProxyEndDateParams {
  extrapolate: boolean;
  proxyType: ProxyTypeEnum;
  unproxiedEndDate: number;
  proxiedStartDate: number;
  unproxiedStartDate: number;
  proxiedFrequency: FrequencyEnum;
  proxiedEndDate: number;
}
export const getProxyEndDate = (proxy: ProxyEndDateParams): number => {
  if (proxy.extrapolate && proxy.proxyType !== 'EXTRAPOLATE' && proxy.proxyType !== 'BACKFILL') {
    // The extrapolation portion of the time series starts at unproxiedEndDate and would be unaffected by other proxies.
    return proxy.unproxiedEndDate;
  }

  switch (proxy.proxyType) {
    case 'BACKFILL':
      if (proxy.proxiedStartDate < proxy.unproxiedStartDate) {
        return (
          Dates.parse(proxy.unproxiedStartDate)
            // @ts-expect-error fixme: ts errors from upgrade to typescript 5.5.3
            .subtract(1, MOMENT_DURATIONS[getFrequencyType(proxy.proxiedFrequency)])
            .valueOf()
        );
      }
      return proxy.unproxiedStartDate;
    case 'INTERPOLATE':
    case 'SUBSTITUTE':
    case 'DESMOOTH':
    case 'DESMOOTH_INTERPOLATE':
    case 'EXTRAPOLATE':
      return proxy.proxiedEndDate;
    default:
      throw assertExhaustive(proxy.proxyType, 'Unexpected proxy type');
  }
};

/**
 * Extracts holdings proxy metadata from a proxy that may have it nested in proxyMetaList.
 * When both returns and holdings proxies are applied, the API returns a single entry with
 * proxyVariant='RETURN' at the top level, with the holdings proxy nested in proxyMetaList.
 */
export const extractHoldingsProxy = (proxy: DetailedProxyMetadata): DetailedProxyMetadata | undefined => {
  if (proxy.proxyVariant === 'HOLDING') {
    return proxy;
  }
  const holdingProxyMetadataIndex = (proxy.proxyMetaList ?? []).findIndex(
    (proxyMeta) => proxyMeta.proxyVariant === 'HOLDING',
  );
  if (holdingProxyMetadataIndex !== -1) {
    const holdingsProxyMetadata = proxy.proxyMetaList[holdingProxyMetadataIndex];
    return {
      ...proxy,
      ...holdingsProxyMetadata,
      proxyName: proxy.proxyNameList?.[holdingProxyMetadataIndex],
    } as DetailedProxyMetadata;
  }
  return undefined;
};

/** Type guard that determines whether the given string is a valid \`ProxyTypeEnum\` value.
 *  @param value The string to check against the \`ProxyTypeEnum\` values.
 *  @returns \`true\` if \`value\` matches one of the enum members, otherwise \`false\`.
 */
export function isProxyTypeEnum(value: string): value is ProxyTypeEnum {
  return Object.values(ProxyTypeEnumValues).includes(value as ProxyTypeEnum);
}

/**
 * Determines whether any of the given proxy types include interpolation.
 * @param proxyTypes - The list of proxy types to check.
 * @returns `true` if at least one proxy type has interpolation, otherwise `false`.
 */
export const hasInterpolateProxyType = (proxyTypes: (ProxyTypeEnum | undefined)[]): boolean => {
  return proxyTypes.filter((proxyType) => !!proxyType).some((proxyType) => proxyType === 'INTERPOLATE');
};
